/**
 * WHAT YOU JUST SENT AN AGENT, until the transcript catches up.
 *
 * Owner, 2026-09-16: 使用者输入的文字内容并没有即时的显示在 Agent 的 CleanView 里面 …
 * 該 Agent 還沒有任何的回答, 使用者輸入的文字就全部都被吃掉了。當該 Agent 開始輸出之後,
 * 使用者的文字以及 Agent 本身的文字又可以被看到.
 *
 * Both halves of that sentence have one cause. An agent's composer does not post a
 * message row — "its input goes into the pty through the two-stage flow, even in
 * claude-view" (App.tsx) — so there is nothing stored to render. Clean View draws
 * the TRANSCRIPT, and claude writes its transcript when a message COMPLETES, which
 * the edge already says in as many words (cloudPty.ts ACT_GAP_MS: "Claude writes its
 * transcript when the message completes — which is when the bytes stop"). So between
 * pressing send and the reply landing there is genuinely nothing on disk to show,
 * and when the file is finally written the prompt and the answer appear together.
 *
 * Project Chat has never had this problem because it echoes the stored row straight
 * into the transcript (projectEcho.ts). This is that, for the surface that has no
 * stored row: hold the text the client itself just sent, and draw it until the real
 * record arrives.
 *
 * KEYED BY SESSION, and only one. A second send before the first lands replaces it —
 * the agent's own input box is where a queued message is visible, and two optimistic
 * rows would be a queue this store is not modelling.
 *
 * NOT the pane store. That one carries the AGENT's in-flight words and is refused,
 * on the edge, when it reproduces the person's own message (liveHub's guard, same
 * day) — putting the owner's text through it would be feeding the exact leak that
 * was just closed.
 */
type Echo = { session: string; text: string; at: number };

let echo: Echo | null = null;
let version = 0;
const subs = new Set<() => void>();

const bump = (): void => {
  version += 1;
  for (const cb of [...subs]) cb();
};

/**
 * How long an unreconciled echo stays on screen.
 *
 * It is a claim that a message was accepted, not that it was answered, so it must
 * not outlive the evidence indefinitely: an agent that never writes a transcript
 * record (a CLI that does not keep one, a send that was queued behind a long task)
 * would otherwise leave a row asserting something nobody can check. Ten minutes is
 * far longer than any write delay measured here and short enough that a stale row
 * cannot survive a session.
 */
const ECHO_MAX_MS = 10 * 60 * 1000;

/** The client just handed this text to an agent. */
export function setSentEcho(session: string, text: string): void {
  const t = text.trim();
  if (!t) return;
  echo = { session, text: t, at: Date.now() };
  bump();
}

/** The transcript now holds it (or the view moved on). */
export function clearSentEcho(): void {
  if (!echo) return;
  echo = null;
  bump();
}

/** What to draw for this session, or null. Expiry is applied on READ so a stale
 *  row cannot be rendered even if nothing has happened to clear it. */
export function sentEchoFor(session: string): string | null {
  if (!echo || echo.session !== session) return null;
  if (Date.now() - echo.at > ECHO_MAX_MS) return null;
  return echo.text;
}

export function sentEchoVersion(): number {
  return version;
}

export function subscribeSentEcho(cb: () => void): () => void {
  subs.add(cb);
  return () => subs.delete(cb);
}
